/**
 * ControlReadinessIndicator - Control readiness status display
 *
 * Shows whether the farm is ready for a specific control/inspection.
 * Uses calm, affirmative styling for success state per PRD guidelines.
 */

import React from 'react';
import { CheckCircle2, AlertCircle, XCircle } from 'lucide-react';
import './ControlReadinessIndicator.css';

export type ReadinessStatus = 'ready' | 'warning' | 'not-ready';

export interface ControlReadinessIndicatorProps {
  /** Readiness status */
  status: ReadinessStatus;

  /** Optional label (e.g., "6 von 8 erfüllt") */
  label?: string;

  /** Whether to show the status icon */
  showIcon?: boolean;

  /** Size variant */
  size?: 'sm' | 'md' | 'lg';

  /** Display variant */
  variant?: 'badge' | 'inline' | 'card';

  /** Additional CSS class */
  className?: string;
}

const STATUS_CONFIG: Record<ReadinessStatus, { defaultLabel: string; Icon: React.FC<{ className?: string }> }> = {
  ready: {
    defaultLabel: 'Kontrolle bereit',
    Icon: CheckCircle2,
  },
  warning: {
    defaultLabel: 'Teilweise bereit',
    Icon: AlertCircle,
  },
  'not-ready': {
    defaultLabel: 'Nicht bereit',
    Icon: XCircle,
  },
};

export const ControlReadinessIndicator: React.FC<ControlReadinessIndicatorProps> = ({
  status,
  label,
  showIcon = true,
  size = 'md',
  variant = 'badge',
  className = '',
}) => {
  const config = STATUS_CONFIG[status];
  const displayLabel = label ?? config.defaultLabel;
  const { Icon } = config;

  return (
    <div
      className={`readiness-indicator readiness-indicator--${status} readiness-indicator--${size} readiness-indicator--${variant} ${className}`}
      role="status"
      aria-label={displayLabel}
    >
      {showIcon && <Icon className="readiness-indicator__icon" aria-hidden="true" />}
      <span className="readiness-indicator__label">{displayLabel}</span>
    </div>
  );
};

export default ControlReadinessIndicator;
